import { useCallback, useRef, useState } from 'react';
import { API_URL } from '../../api';
import usePolling from '../../utils/usePolling';

// 30 s i synlig flik (beslut Mikael 2026-10-07, A11). Pausar i dold flik och
// hämtar direkt när fliken visas igen eller klockan öppnas.
const INTERVALL_MS = 30000;

/**
 * Pollar olästa notiser för klockan i skalet och visar toast för nya notiser
 * av typerna purchasing, risk_comment och dagbok_export.
 *
 * Utbruten ur Layout.js (A11 steg 1, 2026-10-07), usePolling i steg 2.
 *
 * @param {object|null|undefined} user Inloggad användare (pollar bara när satt)
 * @param {(toast: object) => void} push Toast-funktion från ToastProvider
 * @returns {{ unread: number, setUnread: Function, refresh: () => Promise<void> }}
 */
export default function useUnreadNotifications(user, push) {
  const [unread, setUnread] = useState(0);
  const seenNotifIdsRef = useRef(new Set());
  const notifBootstrappedRef = useRef(false);

  const hamta = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/notifications?unread=true&limit=50`, { credentials: 'include' });
      if (!res.ok) return false;
      const arr = await res.json();
      const items = Array.isArray(arr) ? arr : [];
      if (!notifBootstrappedRef.current) {
        // First load: record current unread without toasting.
        items.forEach((n) => { if (n?.id) seenNotifIdsRef.current.add(n.id); });
        notifBootstrappedRef.current = true;
      } else {
        // Subsequent polls: toast new purchasing notifications.
        for (const n of items) {
          if (!n?.id) continue;
          if (seenNotifIdsRef.current.has(n.id)) continue;
          seenNotifIdsRef.current.add(n.id);
          if (n?.type === 'purchasing' && n?.message) {
            push({ type: 'info', message: n.message });
          }
          // Risk-kommentar (Fas 3): toast + visa i notifikations-bell.
          // Triggas av @mention eller när någon kommenterar pa risk du ager.
          if (n?.type === 'risk_comment' && n?.message) {
            push({ type: 'info', message: n.message });
          }
          // Dagbokens PDF-export i bakgrunden (2026-10-06): popup var man
          // än är i KanPlan. I Dagboken visar exportdialogen sin egen
          // bekräftelse, så där hoppas popupen över.
          if (n?.type === 'dagbok_export' && n?.message && !window.location.hash.startsWith('#dagbok')) {
            push({ type: n.message.startsWith('Dagboks-PDF kunde inte') ? 'error' : 'success', message: n.message, ttl: 8000 });
          }
        }
      }
      setUnread(items.length);
      return true;
    } catch {
      return false;
    }
  }, [push]);

  const { refresh } = usePolling(hamta, { intervalMs: INTERVALL_MS, enabled: Boolean(user) });

  return { unread, setUnread, refresh };
}
